import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPublicApi } from '../services/api';
import MenuItemCard from '../components/menu/MenuItemCard';
import { SkeletonCard } from '../components/common/Skeleton';

const DEFAULT_HERO_BG = '';
const DEFAULT_HERO_TITLE = '';
const DEFAULT_HERO_TITLE2 = '';
const DEFAULT_HERO_SUB = '';

const useCountUp = (target, duration = 1800, inView = false) => {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!inView) return;
    const num = parseFloat(target.toString().replace(/[^0-9.]/g, ''));
    if (!num) { setCount(target); return; }
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const suffix = target.toString().replace(/[0-9.]/g, '');
      setCount(Math.floor(eased * num) + suffix);
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, target, duration]);
  return count;
};

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const StatItem = ({ value, label, delay, inView }) => {
  const animated = useCountUp(value, 1600, inView);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="text-center text-white"
    >
      <div className="font-display text-4xl font-bold tracking-tight">
        {inView ? animated : '0'}
      </div>
      <div className="text-espresso-100/80 text-sm mt-1">{label}</div>
    </motion.div>
  );
};

const StatsSection = ({ branding }) => {
  const [inView, setInView] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: branding.stat_customers, label: 'Happy Customers' },
    { value: branding.stat_menu_items, label: 'Menu Items' },
    { value: branding.stat_years, label: 'Years of Excellence' },
    { value: branding.stat_awards, label: 'Awards Won' },
  ];

  return (
    <section ref={ref} className="bg-espresso-500 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <StatItem key={stat.label} value={stat.value} label={stat.label} delay={i * 0.12} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const { slug } = useParams();
  const base = `/${slug}`;
  const [featured, setFeatured] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [brandingLoading, setBrandingLoading] = useState(true);
  const [branding, setBranding] = useState({
    hero_bg_url: '',
    hero_title: '',
    hero_title2: '',
    hero_subtitle: '',
    hero_status_text: 'Now Open',
    hero_status_hours: 'Mon–Fri 7am–10pm',
    stat_customers: '50K+',
    stat_menu_items: '120+',
    stat_years: '8',
    stat_awards: '24',
  });

  useEffect(() => {
    if (!slug) return;
    const pubApi = createPublicApi(slug);
    const cacheKey = `cafe_branding_${slug}`;

    const fetchData = async () => {
      try {
        // Cache se pehle load karo — instant display
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const cachedData = JSON.parse(cached);
          setBranding(cachedData.branding);
          setFeatured(cachedData.featured || []);
          setSpecials(cachedData.specials || []);
          setBrandingLoading(false);
          setLoadingFeatured(false);
        }

        // Fresh data backend se
        const [featRes, specRes, settingsRes] = await Promise.all([
          pubApi.menu.getAll({ featured: 'true', limit: 4 }),
          pubApi.menu.getAll({ special: 'true', limit: 3 }),
          pubApi.settings.get(),
        ]);
        const featured = featRes.data.data || [];
        const specials = specRes.data.data || [];
        const s = settingsRes.data.data || {};
        const newBranding = {
          hero_bg_url: s.hero_bg_url || '',
          hero_title: s.hero_title || '',
          hero_title2: s.hero_title2 || '',
          hero_subtitle: s.hero_subtitle || '',
          hero_status_text: s.hero_status_text || 'Now Open',
          hero_status_hours: s.hero_status_hours || 'Mon–Fri 7am–10pm',
          stat_customers: s.stat_customers || '50K+',
          stat_menu_items: s.stat_menu_items || '120+',
          stat_years: s.stat_years || '8',
          stat_awards: s.stat_awards || '24',
        };
        setFeatured(featured);
        setSpecials(specials);
        setBranding(newBranding);
        setBrandingLoading(false);
        // Cache mein save karo
        sessionStorage.setItem(cacheKey, JSON.stringify({ branding: newBranding, featured, specials }));
      } catch (err) {
        console.error(err);
        setBrandingLoading(false);
      } finally {
        setLoadingFeatured(false);
      }
    };
    fetchData();
  }, [slug]);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {brandingLoading && (<div className="absolute inset-0 bg-bark-950 flex items-center justify-center"><div className="w-12 h-12 border-4 border-espresso-400 border-t-transparent rounded-full animate-spin"></div></div>)}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: brandingLoading ? 'none' : `url(${branding.hero_bg_url || DEFAULT_HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-bark-950/85 via-bark-950/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-bark-950/40 via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            {!brandingLoading && (
              <>
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-espresso-500/20 border border-espresso-400/30 text-espresso-300 text-sm font-medium mb-6"
                >
                  <span className="w-2 h-2 rounded-full bg-espresso-400 animate-pulse" />
                  {branding.hero_status_text} · {branding.hero_status_hours}
                </motion.span>

                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.7 }}
                  className="font-display text-5xl md:text-7xl font-bold text-white leading-tight mb-6"
                >
                  {branding.hero_title}<br />
                  <span className="text-espresso-400 italic">{branding.hero_title2}</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="text-cream-200/80 text-lg md:text-xl leading-relaxed mb-10 max-w-lg"
                >
                  {branding.hero_subtitle}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.6 }}
                  className="flex flex-wrap gap-4"
                >
                  <Link to={`${base}/order`} className="btn-primary text-base px-8 py-4 shadow-lg shadow-espresso-900/30">
                    Order Now
                  </Link>
                  <Link to={`${base}/reservation`} className="btn-secondary border-white/50 text-white hover:bg-white hover:text-bark-900 text-base px-8 py-4">
                    Reserve a Table
                  </Link>
                </motion.div>
              </>
            )}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50"
        >
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-px h-8 bg-white/30"
          />
        </motion.div>
      </section>

      <StatsSection branding={branding} />

      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} viewport={{ once: true }} whileInView="animate" initial="initial"
          className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
          <div>
            <p className="text-espresso-500 font-medium text-sm uppercase tracking-widest mb-2">Our Offerings</p>
            <h2 className="section-title">Featured Menu</h2>
          </div>
          <Link to={`${base}/menu`} className="text-espresso-600 font-medium hover:underline flex items-center gap-1">
            View Full Menu →
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {loadingFeatured
            ? Array(4).fill(0).map((_, i) => <SkeletonCard key={i} />)
            : featured.map((item, i) => (
                <motion.div key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}>
                  <MenuItemCard item={item} />
                </motion.div>
              ))
          }
        </div>
      </section>

      {specials.length > 0 && (
        <section className="bg-bark-900 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeUp} viewport={{ once: true }} whileInView="animate" initial="initial" className="text-center mb-12">
              <p className="text-espresso-400 font-medium text-sm uppercase tracking-widest mb-2">Don't Miss Out</p>
              <h2 className="font-display text-4xl font-bold text-cream-100">Today's Special</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {specials.map((item, i) => (
                <motion.div key={item.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}>
                  <MenuItemCard item={item} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="relative overflow-hidden bg-espresso-600 py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-white translate-y-1/2 -translate-x-1/4" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.h2 {...fadeUp} viewport={{ once: true }} whileInView="animate" initial="initial"
            className="font-display text-4xl md:text-5xl font-bold mb-6">
            Ready to Experience<br />the Perfect Cup?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-espresso-100 text-lg mb-10 max-w-lg mx-auto">
            Reserve your table or place an order online. We'll have everything ready for you.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap justify-center gap-4">
            <Link to={`${base}/reservation`} className="bg-white text-espresso-700 hover:bg-cream-100 font-semibold px-8 py-4 rounded-xl transition-all shadow-lg">
              Book a Table
            </Link>
            <Link to={`${base}/order`} className="border-2 border-white/60 text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-xl transition-all">
              Order Online
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;